import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { UserDataResponse } from '../interfaces/user-data-response.interface';
import { UserDataDetailResponse } from '../interfaces/user-data-detail-response.interface';
import { UserDataDetailUpdateRequest } from '../interfaces/user-data-detail-update-request.interface';
import { ModalService } from './modal.service';
import { UserDataUpdateRequest } from '../../profile/interfaces/user-data-update-request.interface';
import { UserCedisUpdateRequest } from '../../profile/interfaces/user-cedis-update-request.interface';
import { UserRecognitionsDiplomasResponse } from '../interfaces/user-recognitions-diplomas-response.interface';
import { GetUserLevels } from '../interfaces/get-user-levels.interface';
import { UserPointsHistoryResponse } from '../interfaces/user-points-history-response.interface';
import { UserHistoryAwardResponse } from '../interfaces/user-history-award-response.interface';
import { IRedirectWeb, IRedirectWebResponse } from '../../incognito/interfaces/redirect-web.interface';
import { IHelpQuestions } from '../../help/interfaces/help-questions.interface';
import { AccessToken } from '@okta/okta-auth-js';
import { GetRequestTracketing } from '../interfaces/get-request-tracketing.interface';
import { ISurvey, SubmitSurveyPayload } from '../../surveys/interface/surveys.interface';

const API_URL = environment.apiUrl;

@Injectable({ providedIn: 'root' })
export class UserService {
  private http = inject(HttpClient);
  private modalService = inject(ModalService);

  #userUrl = `${API_URL}/users`;

  getUserData(): Observable<UserDataResponse> {
    return this.http.get<UserDataResponse>(`${this.#userUrl}/getUserData`);
  }

  getUserDataDetail(): Observable<UserDataDetailResponse> {
    return this.http.get<UserDataDetailResponse>(
      `${this.#userUrl}/getUserDataDetail`
    );
  }

  getUserDataDetailByGPID(gpid: string): Observable<UserDataDetailResponse> {
    const params = new HttpParams().set('GPID', gpid);
    return this.http.get<UserDataDetailResponse>(
      `${this.#userUrl}/getUserDataDetailByGpid`, {
      params,
    }
    ).pipe(
      map((response: UserDataDetailResponse) => {
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        this.modalService.showErrorModal(error);
        throw error;
      })
    );
  }

  getUsersDataDetailByGPID(gpid: string): Observable<UserDataDetailResponse[]> {
    const params = new HttpParams().set('GPID', gpid);
    return this.http.get<UserDataDetailResponse[]>(
      `${this.#userUrl}/getUsersDataDetailByGpid`, {
      params,
    }
    ).pipe(
      map((response: UserDataDetailResponse[]) => {
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        this.modalService.showErrorModal(error);
        throw error;
      })
    );
  }

  updateUseDataDetail(
    request: UserDataDetailUpdateRequest
  ): Observable<AccessToken> {
    return this.http.post<AccessToken>(`${this.#userUrl}/update/dataDetail`, request).pipe(
      map(resp => resp),
      catchError((err: HttpErrorResponse) => {
        
        return throwError(() => err);
      })
    );
  }

  updateUserData(request: UserDataUpdateRequest): Observable<AccessToken> {
    return this.http.post<AccessToken>(`${this.#userUrl}/update/data`, request).pipe(
      map(resp => resp),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return throwError(() => err);
      })
    );
  }

  updateUserPhoto(request: FormData): Observable<boolean> {
    return this.http.post(`${this.#userUrl}/photo/update`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  updateUserCedis(request: UserCedisUpdateRequest): Observable<boolean> {
    return this.http.post(`${this.#userUrl}/update/cedis`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  getUserRecognitionsDiploma(): Observable<UserRecognitionsDiplomasResponse> {
    return this.http.get<UserRecognitionsDiplomasResponse>(
      `${this.#userUrl}/getUserRecognitionsDiploma`
    );
  }

  getUserLevels(): Observable<GetUserLevels[]> {
    return this.http.get<GetUserLevels[]>(`${this.#userUrl}/getLevels`);
  }

  getUserHistoryAwards(): Observable<UserHistoryAwardResponse[]> {
    return this.http.get<UserHistoryAwardResponse[]>(
      `${this.#userUrl}/history/awards`
    );
  }

  getUserHistoryPointsByCatContestId(
    catContestId: string
  ): Observable<UserPointsHistoryResponse[]> {
    return this.http.get<UserPointsHistoryResponse[]>(
      `${this.#userUrl}/history/points/${catContestId}`
    );
  }

  registerRedirectWeb(payload:IRedirectWeb): Observable<IRedirectWebResponse> {
    return this.http.post<IRedirectWebResponse>(`${this.#userUrl}/registerImpersonationSession`, payload).pipe(
      map(response => response),
      catchError((error: HttpErrorResponse) => {
        this.modalService.showErrorModal(error);
        throw error;
      })
    );
  }

  getUserRecognitionsDiplomaImage() {
    return this.http.get(
      `${this.#userUrl}/getUserRecognitionsDiplomaImage`, {
        responseType: 'blob'
      });
  }
  
  getUserRecognitionsDiplomaPDF() {
    return this.http.get(
      `${this.#userUrl}/getUserRecognitionsDiplomaPdf`, {
        responseType: 'blob'
      });
  }
  
  addSupportResquest(request: FormData): Observable<boolean> {
    return this.http.post(`${API_URL}/addSupportRequest`, request).pipe(
      map(() => true),
      catchError((err: HttpErrorResponse) => {
        this.modalService.showErrorModal(err);
        return of(false);
      })
    );
  }

  generateTempToken(): Observable<string> {
    return this.http.get<string>(`${this.#userUrl}/temp`)
      .pipe(
        map(response => response),
        catchError((error: HttpErrorResponse) => {
          this.modalService.showErrorModal(error);
          throw error;
        })
      )
  }

  isUserParticipatingInRascaYGana(): Observable<boolean> {
    return this.http.get<boolean>(`${this.#userUrl}/rasca-y-gana/isParticipating`)
      .pipe(
        map(response => response),
        catchError((error: HttpErrorResponse) => {
          this.modalService.showErrorModal(error);
          throw error;
        })
      )
  }

  isUserParticipatingInMaraton(): Observable<boolean> {
    return this.http.get<boolean>(`${this.#userUrl}/maraton/isParticipating`)
      .pipe(
        map(response => response),
        catchError((error: HttpErrorResponse) => {
          this.modalService.showErrorModal(error);
          throw error;
        })
      )
  }

  isBirthdayMonth(): Observable<boolean> {
    return this.http.get<boolean>(`${this.#userUrl}/isBirthdayMonth`)
      .pipe(
        map(response => response),
        catchError((error: HttpErrorResponse) => {
          this.modalService.showErrorModal(error);
          throw error;
        })
      )
  }

  getUserDataDetailById(userId: string): Observable<UserDataDetailResponse> {
    const params = new HttpParams().set('UserId', userId);
    return this.http.get<UserDataDetailResponse>(
      `${this.#userUrl}/getUserDataDetailById`, {
      params,
    }
    ).pipe(
      map((response: UserDataDetailResponse) => {
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        this.modalService.showErrorModal(error);
        throw error;
      })
    );
  }

  getRequestTracketing(): Observable<GetRequestTracketing[]> {
    return this.http.get<GetRequestTracketing[]>(`${this.#userUrl}/requestsTracking`);
  }

  sendRequestTracketing(formData:FormData): Observable<GetRequestTracketing|undefined> {
    return this.http.post<GetRequestTracketing>(`${this.#userUrl}/createRequestTracking`, formData).pipe(
      map(response => response),
      catchError((error: HttpErrorResponse) => {
        this.modalService.showErrorModal(error);
        return of(undefined);
      })
    );
  }

  generateTempTokenMaraton(): Observable<string> {
    return this.http.get<string>(`${this.#userUrl}/getTokenMaraton`)
      .pipe(
        map(response => response),
        catchError((error: HttpErrorResponse) => {
          this.modalService.showErrorModal(error);
          throw error;
        })
      )
  }

}